import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import FormPageHeader from 'components/common/form/renderer/page/FormPageHeader';
import FormTopControlPanel from 'components/common/form/top-control-panel/FormTopControlPanel';
import TabsHeader from 'components/common/form/tab/TabsHeader';
import FormTabsContent from 'components/common/form/layout/tab/FormTabsContent';
import PanelBackButton from 'components/common/form/panel-back-button/PanelBackButton';
import SharedControls from 'components/common/form/shared-controls/SharedControls';
import { FormLayoutContext, IFormLayoutContext } from 'components/common/form/layout/tab';
import { ActAfterSaveSuccess } from 'components/common/form/act';

export default function GraphNodeForm(props: IFormLayoutProps) {
    const { elementProps, tabs, hookForm, panelSettings, componentsMap, sharedComponents, customTabs, isNew, t } =
        props;

    const queryClient = useQueryClient();

    hookForm.form.formAct.overrideAct(
        'afterSaveSuccess',
        class extends ActAfterSaveSuccess {
            async exec(response: any, data: any): Promise<any> {
                queryClient.invalidateQueries({ queryKey: ['knowledge-graph', 'panel-data'] });
                return super.exec(response, data);
            }
        },
    );

    const rightTabs = {
        ...tabs,
        formTabs: tabs.formTabs.filter((tab) => tab.name !== 'graph'),
    };
    const showTabs = rightTabs.formTabs.length > 1;
    const layoutContextValue = useMemo<IFormLayoutContext>(
        () => ({ formLayout: 'page', formType: isNew ? 'new' : 'edit' }),
        [isNew],
    );

    return (
        <form
            autoComplete="off"
            onSubmit={hookForm.form.hookFormHandleSubmit((data) => hookForm.form.formAct.act.submit.exec(data))}
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            {!isNew && (
                <FormPageHeader
                    tabsHeader={
                        showTabs ? (
                            <TabsHeader
                                formActions={hookForm.form}
                                tabs={rightTabs.formTabs}
                                onChange={rightTabs.handleTabChange}
                                t={t}
                            />
                        ) : (
                            <></>
                        )
                    }
                    formTopControlPanel={
                        <Box sx={{ py: 0.75 }}>
                            <FormTopControlPanel panelSettings={panelSettings} formActions={hookForm.form} />
                        </Box>
                    }
                    panelBackButton={<PanelBackButton settings={panelSettings?.backButton} />}
                />
            )}
            {sharedComponents.length > 0 && (
                <SharedControls elementProps={elementProps} components={sharedComponents} />
            )}
            <FormLayoutContext.Provider value={layoutContextValue}>
                <FormTabsContent
                    componentsMap={componentsMap}
                    formTabs={rightTabs.formTabs}
                    elementProps={elementProps}
                    customTabs={customTabs}
                />
            </FormLayoutContext.Provider>
        </form>
    );
}
